/**
 * Hero ustidagi tizim ogohlantirishlari (ЧТЗ 4.2.11.1) — iPhone
 * bildirishnomalari uslubida.
 *
 * Sahifa ochilgach xabar(lar) hero tepasida, markazda «prujina» bilan
 * tushib keladi (bir nechta bo'lsa — ketma-ket, ustma-ust kartochkalar),
 * bir necha soniya turadi, so'ng yuqoriga yig'ilib, kichik shisha
 * «kapsula»ga aylanadi (Dynamic Island kabi). Kapsulani bosib xabarni
 * qayta ochish mumkin — yong'in xavfi kabi muhim ma'lumot yo'qolib
 * ketmaydi. Kursor yoki fokus kartochka ustida bo'lsa avtomatik
 * yig'ilmaydi; `Esc` — darhol yig'adi.
 *
 * Shisha uslubi hero ostidagi statistika paneli bilan bir xil. Rang yagona
 * indikator emas: ikonka, daraja yozuvi va matn ham holatni bildiradi.
 * `prefers-reduced-motion` yoqilgan bo'lsa — faqat yumshoq paydo bo'lish.
 */
import { useEffect, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent } from "react";

import { prefersReducedMotion } from "@/shared/lib/motion";
import { Icon } from "@/shared/ui/Icon";
import type { IconName } from "@/shared/ui/Icon";

export interface HeroNotice {
  id: string;
  level: "info" | "warning" | "critical";
  icon: IconName;
  title: string;
  text?: string;
  action?: { label: string; href: string };
}

/** Sahifa ochilgandan keyin xabar tushib kelguncha (hero chizilib ulgursin). */
const APPEAR_DELAY_MS = 900;
/** Xabar ochiq turadigan vaqt (kursor/fokus ustida bo'lmasa). */
const SHOW_MS = 8000;
/** Yig'ilish animatsiyasi va kartochkalar orasidagi kechikish — CSS bilan bir xil. */
const CLOSE_MS = 380;
const STAGGER_MS = 60;

const LEVEL_LABEL: Record<HeroNotice["level"], string> = {
  critical: "Muhim",
  warning: "Ogohlantirish",
  info: "Xabar",
};

type Phase = "idle" | "open" | "closing" | "pill";

export function HeroNotices({ notices }: { notices: HeroNotice[] }) {
  const [phase, setPhase] = useState<Phase>("idle");
  /* Kursor yoki fokus kartochka ustida — avtomatik yig'ilmaydi. */
  const [hold, setHold] = useState(false);
  /* Foydalanuvchi o'zi yig'sa (tugma/Esc) — fokus kapsulaga o'tadi, yo'qolib qolmaydi. */
  const focusPill = useRef(false);
  const pillRef = useRef<HTMLButtonElement>(null);
  const noticeKey = notices.map((notice) => notice.id).join("|");
  const count = notices.length;

  // yangi xabar kelganda (yoki sahifa ochilganda) qaytadan tushib keladi
  useEffect(() => {
    setPhase("idle");
    if (!noticeKey) return;
    const timer = window.setTimeout(() => setPhase("open"), APPEAR_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [noticeKey]);

  useEffect(() => {
    if (phase !== "open" || hold) return;
    const timer = window.setTimeout(() => setPhase("closing"), SHOW_MS);
    return () => window.clearTimeout(timer);
  }, [phase, hold]);

  useEffect(() => {
    if (phase !== "pill" || !focusPill.current) return;
    focusPill.current = false;
    pillRef.current?.focus();
  }, [phase]);

  useEffect(() => {
    if (phase !== "closing") return;
    const duration = prefersReducedMotion() ? 200 : CLOSE_MS + (count - 1) * STAGGER_MS;
    const timer = window.setTimeout(() => setPhase("pill"), duration);
    return () => window.clearTimeout(timer);
  }, [phase, count]);

  if (count === 0) return null;

  const collapse = () => {
    focusPill.current = true;
    setPhase("closing");
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && phase === "open") {
      event.stopPropagation();
      collapse();
    }
  };
  const first = notices[0];

  return (
    <div
      className="hero-notices"
      data-phase={phase}
      style={{ "--n": count } as CSSProperties}
      role="region"
      aria-label="Tizim ogohlantirishlari"
      onMouseEnter={() => setHold(true)}
      onMouseLeave={() => setHold(false)}
      onFocus={() => setHold(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setHold(false);
      }}
      onKeyDown={onKeyDown}
    >
      {phase === "open" || phase === "closing" ? (
        <div className="hn-stack">
          {notices.map((notice, index) => (
            <article
              key={notice.id}
              className={`hn-card is-${notice.level}`}
              style={{ "--i": index } as CSSProperties}
              role={notice.level === "critical" ? "alert" : "status"}
            >
              <span className="hn-icon" aria-hidden="true">
                <Icon name={notice.icon} size={16} />
              </span>
              <div className="hn-body">
                <div className="hn-meta">
                  <span>{LEVEL_LABEL[notice.level]}</span>
                  <span>hozir</span>
                </div>
                <b>{notice.title}</b>
                {notice.text ? <p>{notice.text}</p> : null}
              </div>
              <div className="hn-actions">
                {notice.action ? (
                  <a className="hn-action" href={notice.action.href}>
                    {notice.action.label}
                  </a>
                ) : null}
                {index === 0 ? (
                  <button
                    type="button"
                    className="hn-close"
                    aria-label="Ogohlantirishlarni yig'ish"
                    title="Yig'ish"
                    onClick={collapse}
                  >
                    <Icon name="close" size={14} />
                  </button>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      ) : null}

      {phase === "pill" ? (
        <button
          ref={pillRef}
          type="button"
          className={`hn-pill is-${first.level}`}
          aria-expanded={false}
          aria-label={`Ogohlantirishlarni ochish (${count} ta): ${first.title}`}
          onClick={() => setPhase("open")}
        >
          <span className="hn-dot" aria-hidden="true" />
          <span className="hn-pill-text">{first.title}</span>
          {count > 1 ? <span className="hn-count">+{count - 1}</span> : null}
        </button>
      ) : null}
    </div>
  );
}
